<script lang="ts" setup>
  import { computed } from 'vue'

  // Define props with defaults
  const props = withDefaults(
    defineProps<{
      color?: string
      darkFactor?: number
      lightFactor?: number
      size?: string
      direction?: 'horizontal' | 'vertical'
    }>(),
    {
      color: '#0e2639',
      darkFactor: 0.3,
      lightFactor: 0.2,
      size: '2px',
      direction: 'horizontal'
    }
  )

  // Function to adjust color brightness
  function adjustColorBrightness(color: string, factor: number): string {
    try {
      // Handle hex colors
      if (color.startsWith('#')) {
        // Handle both 3-digit and 6-digit hex
        const hex = color.slice(1)
        const isShortHex = hex.length === 3

        // Parse the hex values
        let r = parseInt(isShortHex ? hex[0] + hex[0] : hex.slice(0, 2), 16)
        let g = parseInt(isShortHex ? hex[1] + hex[1] : hex.slice(2, 4), 16)
        let b = parseInt(isShortHex ? hex[2] + hex[2] : hex.slice(4, 6), 16)

        // Adjust brightness
        if (factor > 0) {
          // Lighten
          r = Math.min(255, r + Math.round((255 - r) * factor))
          g = Math.min(255, g + Math.round((255 - g) * factor))
          b = Math.min(255, b + Math.round((255 - b) * factor))
        } else {
          // Darken
          factor = Math.abs(factor)
          r = Math.max(0, r - Math.round(r * factor))
          g = Math.max(0, g - Math.round(g * factor))
          b = Math.max(0, b - Math.round(b * factor))
        }

        // Convert back to hex
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      }

      // If not a hex color, return the original
      return color
    } catch (e) {
      console.error('Error adjusting color brightness:', e)
      return color
    }
  }

  // Computed properties for the darker and lighter colors
  const darkerColor = computed(() => adjustColorBrightness(props.color, -props.darkFactor))
  const lighterColor = computed(() => adjustColorBrightness(props.color, props.lightFactor))

  // Computed classes and styles based on direction
  const isHorizontal = computed(() => props.direction === 'horizontal')

  const containerClass = computed(() => (isHorizontal.value ? 'flex flex-col' : 'flex flex-row'))

  const firstDividerClass = computed(() => (isHorizontal.value ? 'w-full rounded-t-full' : 'h-full rounded-l-full'))

  const secondDividerClass = computed(() => (isHorizontal.value ? 'w-full rounded-b-full' : 'h-full rounded-r-full'))

  const firstDividerStyle = computed(() => ({
    backgroundColor: darkerColor.value,
    [isHorizontal.value ? 'height' : 'width']: props.size
  }))

  const secondDividerStyle = computed(() => ({
    backgroundColor: lighterColor.value,
    [isHorizontal.value ? 'height' : 'width']: props.size
  }))
</script>

<template>
  <div :class="containerClass">
    <div :class="firstDividerClass" :style="firstDividerStyle"></div>
    <div :class="secondDividerClass" :style="secondDividerStyle"></div>
  </div>
</template>
